/**
 * Free gift with purchase (Halloween 2026: Glow-in-the-Dark Pocket Sluice on orders $250+).
 *
 * Shopify's Buy X Get Y discount makes the gift $0 at checkout but never puts it in the cart, so this
 * keeps the cart in step with the offer: it adds the gift once the eligible subtotal reaches the
 * threshold, removes it again if the subtotal drops back under, and leaves it alone once the customer
 * has removed it themselves. Config comes from snippets/free-gift.liquid (window.PDFreeGift). The gift
 * line is identified by variant id AND the `_free_gift` line property, so a glow sluice someone buys on
 * purpose is never touched. Uses the theme's documented hooks (see assets/custom.js header):
 * `product:added` to re-render after an add, `cart:refresh` after a removal.
 */
(function () {
  var cfg = window.PDFreeGift;
  if (!cfg || !cfg.active) {
    return;
  }

  var DECLINED_KEY = 'pd-free-gift-declined';
  var busy = false;
  var timer = null;
  var lastHadGift = null;

  function declined() {
    try { return sessionStorage.getItem(DECLINED_KEY) === '1'; } catch (e) { return false; }
  }

  function setDeclined() {
    try { sessionStorage.setItem(DECLINED_KEY, '1'); } catch (e) { /* private mode: fall back to in-memory */ }
    cfg.declined = true;
  }

  function isGiftLine(item) {
    return item.variant_id === cfg.variantId && item.properties && item.properties._free_gift === '1';
  }

  function fetchJson(url, options) {
    return fetch(url, Object.assign({ credentials: 'same-origin' }, options || {})).then(function (response) {
      if (!response.ok) {
        throw new Error('free-gift: ' + url + ' returned ' + response.status);
      }
      return response.json();
    });
  }

  function changeLine(key, quantity) {
    return fetchJson(window.routes.cartChangeUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  // Re-render whatever cart the customer can see: the UpCart drawer when its API is present, and the
  // theme's own cart (drawer or /cart page) through the theme's documented cart:refresh hook.
  function refresh() {
    if (typeof window.upcartRefreshCart === 'function') {
      window.upcartRefreshCart();
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
  }

  function sync() {
    if (busy) {
      return;
    }
    busy = true;

    fetchJson(window.routes.cartUrl + '.js', { headers: { 'Cache-Control': 'no-cache' } }).then(function (cart) {
      var giftLine = null;
      var anyGlowLine = false;
      var eligible = 0;

      // Shopify counts the minimum purchase at full price, before any discount, and the glow sluice itself is
      // outside the qualifying collection, so every glow line (ours, UpCart's reward, or one the customer
      // added) stays out of the total.
      cart.items.forEach(function (item) {
        if (isGiftLine(item)) {
          giftLine = giftLine || item;
        }
        if (item.variant_id === cfg.variantId) {
          anyGlowLine = true;
        } else {
          eligible += item.original_line_price;
        }
      });

      var qualifies = eligible >= cfg.threshold;

      // One per order: UpCart shows a quantity picker on the gift line and a second unit is charged full price.
      if (giftLine && giftLine.quantity > 1) {
        return changeLine(giftLine.key, 1).then(function () { refresh(); });
      }

      // The gift line is still priced: a discount code that cannot combine beat the gift, the sale has ended,
      // or the discount was switched off. Take it out rather than charge $12.95 for a "free" item.
      if (giftLine && cfg.requireDiscount && giftLine.final_line_price > 0) {
        return changeLine(giftLine.key, 0).then(function () { lastHadGift = false; refresh(); });
      }

      // The gift was in the cart on the last pass and is gone now while the cart still qualifies:
      // the customer took it out. Respect that for the rest of the session.
      if (lastHadGift === true && !giftLine && qualifies) {
        setDeclined();
      }
      lastHadGift = !!giftLine;

      // Only add when no glow sluice is in the cart at all: UpCart's tiered reward adds its own line (no
      // property) and removes it again below the threshold, so this script is the fallback, never a second adder.
      if (qualifies && !giftLine && !anyGlowLine && cfg.inStock && !declined() && !cfg.declined) {
        return fetchJson(window.routes.cartAddUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: cfg.variantId, quantity: 1, properties: { _free_gift: '1' } })
        }).then(function () {
          lastHadGift = true;
          refresh();
        });
      }

      if (!qualifies && giftLine) {
        return changeLine(giftLine.key, 0).then(function () {
          lastHadGift = false;
          refresh();
        });
      }
    }).catch(function (error) {
      console.error(error);
    }).then(function () {
      busy = false;
    });
  }

  function scheduleSync() {
    clearTimeout(timer);
    timer = setTimeout(sync, 250);
  }

  // The customer clicked "Remove" on the gift line: remember it before the cart re-renders.
  document.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-action="decrease-quantity"][data-quantity="0"]');
    if (remove && remove.closest('[data-free-gift-line]')) {
      setDeclined();
    }
  });

  // No-JS fallback form in the cart message, upgraded to an ajax add when JS is running.
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-free-gift-form]');
    if (!form) {
      return;
    }
    event.preventDefault();
    try { sessionStorage.removeItem(DECLINED_KEY); } catch (e) { /* ignore */ }
    cfg.declined = false;
    lastHadGift = null;
    sync();
  });

  // theme:loading:end fires on document after every mini-cart / cart page re-render (quantity
  // changes, removals, adds), so the cart is re-checked after each change the customer makes.
  document.addEventListener('theme:loading:end', scheduleSync);
  document.addEventListener('product:added', scheduleSync);

  // The UpCart drawer (and other apps) change the cart without firing the theme's events, so every
  // /cart/add, /cart/change and /cart/update call made on the page is followed by a re-check.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = nativeFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
      result.then(scheduleSync, function () {});
    }
    return result;
  };
  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(String(url))) {
      this.addEventListener('loadend', scheduleSync);
    }
    return nativeOpen.apply(this, arguments);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
